import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { LeadMarketingRow, LeadStatut } from '@/lib/database.types';

// Sprint 7.2 — Leads B2C marketing.
// Workflow : nouveau → repondu → interesse → inscrit (ou perdu / a_relancer).
// La conversion lead → inscription crée automatiquement un tier (client_b2c)
// si nécessaire, et lie l'inscription au lead via inscription_id.

export function useLeadsMarketing(filter: {
  entrepriseId: string | null;
  statut?: LeadStatut | null;
  assignedToUserId?: string | null;
  campagneId?: string | null;
  pageId?: string | null;
}) {
  return useQuery({
    queryKey: ['leads_marketing', filter],
    enabled: !!filter.entrepriseId,
    queryFn: async (): Promise<LeadMarketingRow[]> => {
      if (!filter.entrepriseId) return [];
      let q = supabase
        .from('leads_marketing')
        .select('*')
        .eq('entreprise_id', filter.entrepriseId)
        .order('created_at', { ascending: false });
      if (filter.statut) q = q.eq('statut', filter.statut);
      if (filter.assignedToUserId) q = q.eq('assigned_to_user_id', filter.assignedToUserId);
      if (filter.campagneId) q = q.eq('campagne_id', filter.campagneId);
      if (filter.pageId) q = q.eq('source_page_id', filter.pageId);
      const { data, error } = await q.limit(500);
      if (error) throw error;
      return data as unknown as LeadMarketingRow[];
    },
  });
}

export interface UpsertLeadInput {
  id?: string;
  entreprise_id: string;
  source_page_id?: string | null;
  campagne_id?: string | null;
  assigned_to_user_id?: string | null;
  nom_prospect?: string | null;
  telephone?: string | null;
  statut?: LeadStatut;
  notes?: string | null;
}

export function useUpsertLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertLeadInput): Promise<LeadMarketingRow> => {
      const { id, ...rest } = input;
      if (id) {
        const { data, error } = await supabase
          .from('leads_marketing')
          .update(rest as never)
          .eq('id', id)
          .select('*')
          .single();
        if (error) throw error;
        return data as unknown as LeadMarketingRow;
      }
      const { data, error } = await supabase
        .from('leads_marketing')
        .insert(rest as never)
        .select('*')
        .single();
      if (error) throw error;
      return data as unknown as LeadMarketingRow;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads_marketing'] });
    },
  });
}

export function useDeleteLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('leads_marketing').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads_marketing'] });
    },
  });
}

// ===== Conversion lead → inscription (tier auto-créé si absent) =====

export interface ConvertLeadInput {
  lead: LeadMarketingRow;
  session_id: string;
  prix_facture: number;
  revenu_strategix_dzd: number;
  prime_montant: number;
  montant_acompte: number;
  acompte_ccp_url?: string | null;
  paiement_complete: boolean;
  // Si le tier n'existe pas, on le crée à partir de ces infos
  tier_raison_sociale: string;
  tier_telephone?: string | null;
}

export function useConvertLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ConvertLeadInput): Promise<{ inscriptionId: string }> => {
      const { lead } = input;

      // 1. tier — réutilise existant ou en crée un (type=client_b2c)
      let tierId = lead.tier_id;
      if (!tierId) {
        const { data: createdTier, error: tierErr } = await supabase
          .from('tiers')
          .insert({
            entreprise_id: lead.entreprise_id,
            type: 'client_b2c',
            raison_sociale: input.tier_raison_sociale,
            telephone: input.tier_telephone ?? lead.telephone ?? null,
            is_active: true,
          } as never)
          .select('id')
          .single();
        if (tierErr) throw tierErr;
        tierId = (createdTier as { id: string } | null)?.id ?? null;
        if (!tierId) throw new Error('Création tier sans retour');
      }

      // 2. inscription — avec champs B2C dérivés du sprint 7.0
      const { data: createdInsc, error: inscErr } = await supabase
        .from('inscriptions_formation')
        .insert({
          session_id: input.session_id,
          tier_id: tierId,
          prix_facture: input.prix_facture,
          source: 'b2c_marketing',
          source_page_id: lead.source_page_id,
          revenu_strategix_dzd: input.revenu_strategix_dzd,
          prime_commercial_id: lead.assigned_to_user_id,
          prime_montant: input.prime_montant,
          montant_acompte: input.montant_acompte,
          acompte_ccp_url: input.acompte_ccp_url ?? null,
          paiement_complete: input.paiement_complete,
          statut: 'confirme',
        } as never)
        .select('id')
        .single();
      if (inscErr) throw inscErr;
      const inscriptionId = (createdInsc as { id: string } | null)?.id ?? null;
      if (!inscriptionId) throw new Error('Création inscription sans retour');

      // 3. lead — bascule statut + cross-ref
      const { error: updErr } = await supabase
        .from('leads_marketing')
        .update({
          statut: 'inscrit',
          tier_id: tierId,
          inscription_id: inscriptionId,
        } as never)
        .eq('id', lead.id);
      if (updErr) throw updErr;

      return { inscriptionId };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads_marketing'] });
      void qc.invalidateQueries({ queryKey: ['inscriptions_formation'] });
      void qc.invalidateQueries({ queryKey: ['tiers'] });
      void qc.invalidateQueries({ queryKey: ['v_roi_formation_b2c'] });
      void qc.invalidateQueries({ queryKey: ['v_revenus_strategix'] });
    },
  });
}
